import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLookups } from '../context/LookupContext';
import { useTheme } from '../hooks/useTheme';
import { Save, User, Briefcase, Target, MapPin, Building2, Camera, X, Upload, Moon, Sun, Lock, Eye, EyeOff, FileText, Trash2, Loader2, Pencil, ChevronDown, Plus } from 'lucide-react';
import api from '../api/api';
import MultiSelect from '../components/ui/MultiSelect';
import PreferencesSkeleton from '../components/layout/PreferencesSkeleton';

export default function MyPreferences() {
    //const { user, setUser } = useAuth();
    const { user, setUser, checkAuth } = useAuth();
    const { lookups, loading: lookupsLoading } = useLookups();
    const { isDarkMode, setDarkMode } = useTheme();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    // The email the page loaded (an unchanged email isn't sent on Save)
    const [loadedEmail, setLoadedEmail] = useState('');
    const emailInputRef = useRef(null);
    const [success, setSuccess] = useState('');
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePictureSource, setProfilePictureSource] = useState(null); // 'uploaded' or 'linkedin'
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const fileInputRef = useRef(null);
    const [disconnectingLinkedIn, setDisconnectingLinkedIn] = useState(false);
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

    const [resumes, setResumes] = useState([]);
    const [resumeNickname, setResumeNickname] = useState('');
    const [uploadingResume, setUploadingResume] = useState(false);
    const [resumeError, setResumeError] = useState('');
    const [resumeSuccess, setResumeSuccess] = useState('');
    const [editingResumeId, setEditingResumeId] = useState(null);
    const [editResumeLabel, setEditResumeLabel] = useState('');
    const [deleteResumeConfirm, setDeleteResumeConfirm] = useState(null);
    const [resumeDropdownOpen, setResumeDropdownOpen] = useState(false);
    const [showResumeUploadModal, setShowResumeUploadModal] = useState(false);
    const [pendingResumeFile, setPendingResumeFile] = useState(null);
    const [draggingResume, setDraggingResume] = useState(false);
    const [resumePreviewUrl, setResumePreviewUrl] = useState(null);
    const [resumePreviewName, setResumePreviewName] = useState('');
    const resumeDropdownRef = useRef(null);
    const resumeFileInputRef = useRef(null);
    const RESUME_CAP = 5;
    const ALLOWED_RESUME_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    // Change Password (independent of the main profile form/Save Changes button)
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const newPasswordMeetsRequirements = passwordForm.newPassword.length >= 8 && /[A-Z]/.test(passwordForm.newPassword);
    const confirmPasswordMatches = passwordForm.confirmPassword.length > 0 && passwordForm.newPassword === passwordForm.confirmPassword;

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');
        const { currentPassword, newPassword, confirmPassword } = passwordForm;
        if (!currentPassword || !newPassword) {
            setPasswordError('Please fill in both password fields.');
            return;
        }
        if (newPassword.length < 8 || !/[A-Z]/.test(newPassword)) {
            setPasswordError('New password must be at least 8 characters and contain one uppercase letter.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }
        setPasswordSaving(true);
        try {
            await api.post('/auth/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
            });
            setPasswordSuccess('Password updated successfully.');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPasswordError(err.response?.data?.error || 'Failed to update password.');
        } finally {
            setPasswordSaving(false);
        }
    };

    const fetchResumes = async () => {
        try {
            const res = await api.get('/resume/my-resumes');
            setResumes(res.data.resumes || []);
        } catch { /* silent — resumes section just shows empty */ }
    };

    useEffect(() => { fetchResumes(); }, []);

    // An email error shows under the Email field, far above Save: bring it into view
    useEffect(() => {
        if (emailError && emailInputRef.current) emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [emailError]);

    useEffect(() => {
        const target = location.state?.scrollTo;
        if (!loading && !lookupsLoading && target) {
            const t1 = setTimeout(() => {
                const el = document.getElementById(target);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
            const t2 = setTimeout(() => {
                const el = document.getElementById(target);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 1500);
            return () => { clearTimeout(t1); clearTimeout(t2); };
        }
    }, [loading, lookupsLoading, location.state]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (resumeDropdownRef.current && !resumeDropdownRef.current.contains(e.target)) {
                setResumeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleResumeFileSelect = (file) => {
        if (!file) return;
        if (!ALLOWED_RESUME_TYPES.includes(file.type)) { setResumeError('Only PDF and DOCX files accepted'); return; }
        if (file.size > 5 * 1024 * 1024) { setResumeError('File exceeds 5MB limit'); return; }
        setResumeError('');
        setPendingResumeFile(file);
    };

    const closeResumeUploadModal = () => {
        setShowResumeUploadModal(false);
        setResumeNickname('');
        setPendingResumeFile(null);
        setResumeError('');
    };

    const handlePreviewResume = async (resumeId) => {
        const resume = resumes.find((r) => r.ResumeID === resumeId);
        const fileName = resume?.FileName || '';
        const isPdf = fileName.toLowerCase().endsWith('.pdf');
        try {
            const res = await api.get(`/resume/${resumeId}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            if (isPdf) {
                setResumePreviewUrl(url);
                setResumePreviewName(resume?.NickName || fileName);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch {
            setResumeError('Failed to preview resume');
        }
    };

    const closeResumePreview = () => {
        if (resumePreviewUrl) URL.revokeObjectURL(resumePreviewUrl);
        setResumePreviewUrl(null);
        setResumePreviewName('');
    };

    const handleResumeUploadSubmit = async () => {
        setResumeError('');
        if (!resumeNickname.trim()) { setResumeError('Please enter a label for your resume'); return; }
        if (!pendingResumeFile) { setResumeError('Please select a file'); return; }
        setUploadingResume(true);
        try {
            const formData = new FormData();
            formData.append('resume', pendingResumeFile);
            formData.append('nickname', resumeNickname.trim());
            await api.post('/resume/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setResumeSuccess(`Uploaded "${resumeNickname.trim()}"`);
            closeResumeUploadModal();
            fetchResumes();
            setTimeout(() => setResumeSuccess(''), 3000);
        } catch (err) {
            setResumeError(err.response?.data?.error || 'Upload failed');
        } finally {
            setUploadingResume(false);
        }
    };

    const handleResumeDelete = async (resumeId) => {
        try {
            await api.delete(`/resume/${resumeId}`);
            setDeleteResumeConfirm(null);
            setResumeSuccess('Resume deleted');
            fetchResumes();
            setTimeout(() => setResumeSuccess(''), 3000);
        } catch (err) {
            setResumeError(err.response?.data?.error || 'Failed to delete resume');
        }
    };

    const handleResumeRename = async (resumeId) => {
        if (!editResumeLabel.trim()) return;
        try {
            await api.patch(`/resume/${resumeId}`, { nickName: editResumeLabel.trim() });
            setEditingResumeId(null);
            setEditResumeLabel('');
            setResumeSuccess('Resume renamed');
            fetchResumes();
            setTimeout(() => setResumeSuccess(''), 3000);
        } catch (err) {
            setResumeError(err.response?.data?.error || 'Failed to rename');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Form state
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        linkedinUrl: '',
        persona: '',
        intent: '',
        industries: [],
        positions: [],
        jobLocations: [],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    // Get available positions from lookups
    const availablePositions = React.useMemo(() => {
        if (lookups.positions && Array.isArray(lookups.positions) && lookups.positions.length > 0) {
            return lookups.positions.map(pos => 
                typeof pos === 'string' ? pos : pos.name || pos.PositionName
            );
        }
        // Fallback to default list if positions not in lookups yet
        return [
            'Software Engineer', 'Product Manager', 'Data Scientist', 'UX Designer',
            'Marketing Manager', 'Sales Representative', 'Business Analyst',
            'Project Manager', 'DevOps Engineer', 'Frontend Developer',
            'Backend Developer', 'Full Stack Developer', 'QA Engineer',
            'Technical Writer', 'Product Designer'
        ];
    }, [lookups.positions]);

    // Real locations from the DB, grouped by state ({state, cities: [{id, name}]}).
    // The backend already sorts states and cities alphabetically, so flattening
    // preserves that order.
    const availableLocations = React.useMemo(() => {
        if (!Array.isArray(lookups.locations)) return [];
        return lookups.locations.flatMap((group) =>
            (group.cities || []).map((city) => ({
                id: city.id,
                name: group.state ? `${group.state}, ${city.name}` : city.name,
            }))
        );
    }, [lookups.locations]);

    // Fetch current user data
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                const response = await api.get('/user/me');
                const userData = response.data;

                // Fetch onboarding details for industries, positions, and job locations
                const onboardingRes = await api.get('/user/onboarding');
                const onboardingData = onboardingRes.data;

                // Fetch profile picture
                try {
                    const picResponse = await api.get('/user/profile-picture');
                    if (picResponse.data && picResponse.data.image) {
                        setProfilePicture(picResponse.data.image);
                        setProfilePictureSource(picResponse.data.source || 'uploaded');
                    }
                } catch (picErr) {
                    // No profile picture found - that's okay
                }

                // The email as loaded: only an edited email is sent on Save
                setLoadedEmail(userData.email || '');
                setFormData({
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    // The column doubles as internal LinkedIn identity storage:
                    // NON_LINKEDIN_* is the "no LinkedIn yet" placeholder and a
                    // bare value like "dRG7UjUi5T" is the OAuth member ID — only
                    // real profile URLs are shown (empty saves never overwrite
                    // the internal value, so displaying blank is safe).
                    linkedinUrl: (() => {
                        const v = userData.linkedin_url || '';
                        if (v.toUpperCase().startsWith('NON_LINKEDIN_')) return '';
                        if (v && !/^https?:\/\//i.test(v) && !v.toLowerCase().includes('linkedin.com')) return '';
                        return v;
                    })(),
                    persona: userData.onboarding?.persona || onboardingData.persona || '',
                    intent: userData.onboarding?.intent || onboardingData.intent || '',
                    industries: onboardingData.industries ? 
                        (Array.isArray(onboardingData.industries) ? onboardingData.industries : [onboardingData.industries]) : 
                        [],
                    positions: onboardingData.positions && Array.isArray(onboardingData.positions) ? 
                        onboardingData.positions : 
                        (userData.onboarding?.aspiring_role ? [userData.onboarding.aspiring_role] : []),
                    jobLocations: onboardingData.job_locations && Array.isArray(onboardingData.job_locations) ?
                        onboardingData.job_locations : [],
                    timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
                });

                // Set dark mode from user data if available
                if (userData.dark_mode !== undefined) {
                    setDarkMode(userData.dark_mode);
                }
            } catch (err) {
                console.error('Error fetching user data:', err);
                setError('Failed to load user data');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchUserData();
        }
    }, [user, setDarkMode]);

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setEmailError('');
        setSuccess('');

        // Validation
        if (formData.positions.length === 0) {
            setError('Please select at least one position');
            return;
        }
        // Login and password reset go by email, so it can't be blank or malformed
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        try {
            setSaving(true);

            // Only an email typed here is sent: a page left open elsewhere would
            // otherwise put back an address changed since on another device
            const emailEdited = formData.email.trim().toLowerCase() !== loadedEmail.trim().toLowerCase();

            // Send all updates in a single PATCH request to /user/update_user
            // This will use the UpdateUserPreferences stored procedure
            const response = await api.patch('/user/update_user', {
                // Basic profile fields
                first_name: formData.firstName,
                last_name: formData.lastName,
                //email: formData.email,
                ...(emailEdited ? { email: formData.email } : {}),
                // Prepend the scheme users usually omit; empty stays empty
                // (the backend keeps its placeholder for empty values)
                linkedin_url: formData.linkedinUrl.trim() && !/^https?:\/\//i.test(formData.linkedinUrl.trim())
                    ? `https://${formData.linkedinUrl.trim()}`
                    : formData.linkedinUrl.trim(),
                timezone: formData.timezone,
                // Preference fields (will be handled by stored procedure)
                DarkModeEnabled: isDarkMode,
                PersonaName: formData.persona || '',
                IntentName: formData.intent || '',
                industries: formData.industries, // Array of industry names
                positions: formData.positions, // Array of job role names
                job_locations: formData.jobLocations.slice(0, 3) // Array of LocationIDs (max 3, matching signup)
            });

            // Check if the response indicates success
            if (response.data && (response.data.success || response.data.message)) {
                // Keep the navbar/greeting (which reads from AuthContext, not this
                // page's local state) in sync with the name just saved.
                setUser(prev => ({
                    ...prev,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    //email: formData.email,
                    email: formData.email.trim().toLowerCase(),
                }));
                //setSuccess('Preferences saved successfully!');
                //setTimeout(() => setSuccess(''), 3000);
                if (response.data.emailVerificationRequired) {
                    // New email: it has to be confirmed with the code just sent to
                    // it. Refreshing the account sends them to the code screen.
                    setSuccess(response.data.message || 'We sent a code to your new email.');
                    setTimeout(() => checkAuth(), 2500);
                } else {
                    setSuccess('Preferences saved successfully!');
                    setTimeout(() => setSuccess(''), 3000);
                }
            } else {
                throw new Error('Unexpected response format');
            }
        } catch (err) {
            console.error('Error saving preferences:', err);
            console.error('Error details:', {
                message: err.message,
                response: err.response,
                request: err.request,
                code: err.code
            });
            
            // Handle network errors specifically
            if (err.code === 'ECONNABORTED' || err.message === 'Network Error' || !err.response) {
                setError('Network error: Could not connect to server. Please check if the server is running.');
            } else {
                const errorMessage = err.response?.data?.error ||
                                    err.response?.data?.message ||
                                    err.message ||
                                    'Failed to save preferences';
                // Show the "email already in use" error under the field itself
                // instead of (or in addition to) the generic banner.
                //if (errorMessage.toLowerCase().includes('email is already in use')) {
                //if (errorMessage.toLowerCase().includes('email is already in use') || errorMessage.toLowerCase().includes('valid email')) {
                if (errorMessage.toLowerCase().includes('email is already in use') || errorMessage.toLowerCase().includes('valid email')
                    || errorMessage.toLowerCase().includes('codes to this email')) {
                    setEmailError(errorMessage);
                } else {
                    setError(errorMessage);
                }
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmDisconnectLinkedIn = async () => {
        setShowDisconnectConfirm(false);
        setError('');
        setSuccess('');
        setDisconnectingLinkedIn(true);
        try {
            await api.post('/user/disconnect-linkedin');
            setFormData(prev => ({ ...prev, linkedinUrl: '' }));
            setSuccess('LinkedIn disconnected.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to disconnect LinkedIn.');
        } finally {
            setDisconnectingLinkedIn(false);
        }
    };

    const handlePictureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('File size exceeds 5MB limit. Please choose a smaller image.');
            return;
        }

        try {
            setUploadingPicture(true);
            setError('');
            
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post('/user/profile-picture', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data && response.data.image) {
                setProfilePicture(response.data.image);
                setProfilePictureSource('uploaded');
                // Update user context
                setUser(prev => ({ ...prev, profilePicture: response.data.image }));
                setSuccess('Profile picture uploaded successfully!');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Error uploading profile picture:', err);
            setError(err.response?.data?.error || 'Failed to upload profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleDeletePicture = async () => {
        if (!window.confirm('Are you sure you want to delete your profile picture?')) {
            return;
        }

        try {
            setUploadingPicture(true);
            await api.delete('/user/profile-picture');
            // After deleting uploaded picture, try to get LinkedIn picture if available
            try {
                const picResponse = await api.get('/user/profile-picture?prefer_linkedin=true');
                if (picResponse.data && picResponse.data.image) {
                    setProfilePicture(picResponse.data.image);
                    setProfilePictureSource(picResponse.data.source || 'linkedin');
                    setUser(prev => ({ ...prev, profilePicture: picResponse.data.image }));
                } else {
                    setProfilePicture(null);
                    setProfilePictureSource(null);
                    setUser(prev => ({ ...prev, profilePicture: null }));
                }
            } catch {
                setProfilePicture(null);
                setProfilePictureSource(null);
                setUser(prev => ({ ...prev, profilePicture: null }));
            }
            setSuccess('Profile picture deleted successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error deleting profile picture:', err);
            setError(err.response?.data?.error || 'Failed to delete profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    if (loading || lookupsLoading) {
        return <PreferencesSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`max-w-4xl mx-auto space-y-8 p-6 ${isDarkMode ? 'bg-surface' : ''}`}>
            <header>
                <h1 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>My Account / Preferences</h1>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage your account settings and career preferences.</p>
            </header>

            <form onSubmit={handleSave}>
                <div className={`rounded-3xl border shadow-sm p-4 md:p-8 space-y-6 md:space-y-10 ${
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                }`}>
                    {/* Current User Information */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <User className="text-blue-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Current Information</h3>
                        </div>

                        <div className={`rounded-xl p-4 md:p-6 space-y-4 mb-4 md:mb-6 ${
                            isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'
                        }`}>
                            <div className="grid grid-cols-2 gap-3 md:gap-4">
                                <div className="min-w-0">
                                    <label className={`text-xs md:text-sm font-semibold uppercase tracking-wide mb-1 md:mb-2 block ${
                                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>First Name</label>
                                    <div className={`text-sm md:text-base font-medium break-words ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>{formData.firstName || 'Not set'}</div>
                                </div>
                                <div className="min-w-0">
                                    <label className={`text-xs md:text-sm font-semibold uppercase tracking-wide mb-1 md:mb-2 block ${
                                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>Last Name</label>
                                    <div className={`text-sm md:text-base font-medium break-words ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>{formData.lastName || 'Not set'}</div>
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <label className={`text-xs md:text-sm font-semibold uppercase tracking-wide mb-1 md:mb-2 block ${
                                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>Email</label>
                                    <div className={`text-sm md:text-base font-medium break-words ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>{formData.email || 'Not set'}</div>
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <label className={`text-xs md:text-sm font-semibold uppercase tracking-wide mb-1 md:mb-2 block ${
                                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                    }`}>Current Persona</label>
                                    <div className={`text-sm md:text-base font-medium break-words ${isDarkMode ? 'text-slate-200' : 'text-slate-900'}`}>{formData.persona || 'Not set'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>First Name</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode 
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-500/20 focus:border-blue-500' 
                                            : 'border-slate-200 bg-white focus:ring-blue-50'
                                    }`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Last Name</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode 
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-500/20 focus:border-blue-500' 
                                            : 'border-slate-200 bg-white focus:ring-blue-50'
                                    }`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Email</label>
                                <input
                                    ref={emailInputRef}
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        setFormData({ ...formData, email: e.target.value });
                                        if (emailError) setEmailError('');
                                    }}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? `border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-500/20 focus:border-blue-500 ${emailError ? 'border-red-500' : ''}`
                                            : `border-slate-200 bg-white focus:ring-blue-50 ${emailError ? 'border-red-300' : ''}`
                                    }`}
                                />
                                {emailError && (
                                    <p className="text-sm text-red-500">{emailError}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Persona</label>
                                <select
                                    value={formData.persona}
                                    onChange={(e) => setFormData({ ...formData, persona: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode 
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-blue-500/20 focus:border-blue-500' 
                                            : 'border-slate-200 bg-white focus:ring-blue-50'
                                    }`}
                                >
                                    <option value="">Select Persona</option>
                                    {Object.keys(lookups.personas || {})
                                        // "testing adding" / "Persona_Default" are seeded placeholder
                                        // rows in the Personas table, not real selectable personas.
                                        .filter(p => !['testingadding', 'personadefault'].includes(p.replace(/[\s_]/g, '').toLowerCase()))
                                        .map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>LinkedIn URL</label>
                                <input
                                    type="text"
                                    value={formData.linkedinUrl}
                                    onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
                                    placeholder="https://linkedin.com/in/username"
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 placeholder-slate-500 focus:ring-blue-500/20 focus:border-blue-500'
                                            : 'border-slate-200 bg-white placeholder-slate-400 focus:ring-blue-50'
                                    }`}
                                />
                                {formData.linkedinUrl.trim() && (
                                    <button
                                        type="button"
                                        onClick={() => setShowDisconnectConfirm(true)}
                                        disabled={disconnectingLinkedIn}
                                        className={`text-xs font-semibold transition-colors disabled:opacity-50 ${
                                            isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                                        }`}
                                    >
                                        {disconnectingLinkedIn ? 'Disconnecting...' : 'Disconnect LinkedIn'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Profile Picture Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <Camera className="text-blue-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Profile Picture</h3>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-start gap-4 md:gap-6">
                            <div className="relative shrink-0 self-center sm:self-start">
                                {profilePicture ? (
                                    <div className="relative group">
                                        <img 
                                            src={profilePicture} 
                                            alt="Profile" 
                                            className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-slate-200 shadow-lg"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleDeletePicture}
                                            disabled={uploadingPicture}
                                            className="absolute top-0 right-0 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                            title="Delete picture"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-slate-200 flex items-center justify-center border-4 border-slate-300">
                                        <User size={48} className="text-slate-400" />
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 space-y-4">
                                <div>
                                    {profilePictureSource && (
                                        <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Current: {profilePictureSource === 'linkedin' ? 'LinkedIn Profile Picture' : 'Uploaded Picture'}
                                        </p>
                                    )}
                                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                        Upload a profile picture (JPEG, PNG, GIF, or WebP). Maximum size: 5MB. 
                                        Image will be automatically resized to 400x400 pixels.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handlePictureUpload}
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingPicture}
                                            className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Upload size={18} />
                                            {uploadingPicture ? 'Uploading...' : profilePicture ? 'Change Picture' : 'Upload Picture'}
                                        </button>
                                        {profilePicture && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const preference = profilePictureSource === 'linkedin' ? 'uploaded' : 'linkedin';
                                                        await api.put('/user/profile-picture-preference', { preference });
                                                        // Refresh profile picture with new preference
                                                        const picResponse = await api.get(`/user/profile-picture?prefer_linkedin=${preference === 'linkedin'}`);
                                                        if (picResponse.data && picResponse.data.image) {
                                                            setProfilePicture(picResponse.data.image);
                                                            setProfilePictureSource(picResponse.data.source || preference);
                                                            setUser(prev => ({ ...prev, profilePicture: picResponse.data.image }));
                                                            setSuccess(`Switched to ${preference === 'linkedin' ? 'LinkedIn' : 'uploaded'} picture!`);
                                                        }
                                                    } catch (err) {
                                                        setError(err.response?.data?.error || 'Failed to switch picture source');
                                                    }
                                                }}
                                                className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                                            >
                                                Use {profilePictureSource === 'linkedin' ? 'Uploaded' : 'LinkedIn'} Picture
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* My Resumes Section */}
                    <section id="resumes" style={{ scrollMarginTop: '100px' }}>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <FileText className="text-primary-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>My Resumes</h3>
                        </div>

                        {resumeError && (
                            <div className={`border px-4 py-3 rounded-lg text-sm mb-4 ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                                {resumeError}
                            </div>
                        )}
                        {resumeSuccess && (
                            <div className={`border px-4 py-3 rounded-lg text-sm mb-4 ${isDarkMode ? 'bg-green-900/30 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-700'}`}>
                                {resumeSuccess}
                            </div>
                        )}

                        {/* Resume dropdown */}
                        <div className="relative" ref={resumeDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setResumeDropdownOpen(!resumeDropdownOpen)}
                                className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-sm transition-colors ${
                                    isDarkMode
                                        ? 'bg-slate-700 border-slate-600 text-slate-100 hover:border-primary-500'
                                        : 'bg-white border-slate-300 text-slate-900 hover:border-primary-500'
                                }`}
                            >
                                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                                    Select a resume
                                </span>
                                <ChevronDown size={16} className={`transition-transform ${resumeDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {resumeDropdownOpen && (
                                <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${
                                    isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
                                }`}>
                                    {resumes.map((r) => (
                                        <div key={r.ResumeID}>
                                            {editingResumeId === r.ResumeID ? (
                                                <div className={`flex items-center gap-2 px-4 py-3 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-50'}`}>
                                                    <input
                                                        type="text"
                                                        value={editResumeLabel}
                                                        onChange={(e) => setEditResumeLabel(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleResumeRename(r.ResumeID);
                                                            if (e.key === 'Escape') setEditingResumeId(null);
                                                        }}
                                                        autoFocus
                                                        maxLength={100}
                                                        className={`flex-1 px-2 py-1 border rounded text-sm ${
                                                            isDarkMode ? 'bg-slate-800 border-slate-500 text-slate-100' : 'border-slate-300 text-slate-900'
                                                        }`}
                                                    />
                                                    <button onClick={() => handleResumeRename(r.ResumeID)} className="text-primary-500 hover:text-primary-600 text-xs font-medium">Save</button>
                                                    <button onClick={() => setEditingResumeId(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                                                </div>
                                            ) : (
                                                <div className={`flex items-center justify-between px-4 py-3 transition-colors ${
                                                    isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-slate-50'
                                                }`}>
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <FileText size={16} className="shrink-0 text-primary-500" />
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium truncate">{r.NickName || r.FileName}</p>
                                                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                {formatFileSize(r.FileSizeBytes)} &middot; {r.CreatedOn ? new Date(r.CreatedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button
                                                            type="button"
                                                            title="View"
                                                            onClick={(e) => { e.stopPropagation(); handlePreviewResume(r.ResumeID); }}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                isDarkMode ? 'text-slate-400 hover:text-primary-400 hover:bg-slate-600' : 'text-slate-400 hover:text-primary-600 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            <Eye size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Rename"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingResumeId(r.ResumeID);
                                                                setEditResumeLabel(r.NickName || r.FileName || '');
                                                            }}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                isDarkMode ? 'text-slate-400 hover:text-primary-400 hover:bg-slate-600' : 'text-slate-400 hover:text-primary-600 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteResumeConfirm({ id: r.ResumeID, name: r.NickName || r.FileName || '' });
                                                            }}
                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                isDarkMode ? 'text-slate-400 hover:text-red-400 hover:bg-slate-600' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                                                            }`}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {resumes.length < RESUME_CAP && (
                                        <div
                                            onClick={() => {
                                                setShowResumeUploadModal(true);
                                                setResumeDropdownOpen(false);
                                            }}
                                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-t ${
                                                isDarkMode
                                                    ? 'border-slate-600 hover:bg-slate-600 text-primary-400'
                                                    : 'border-slate-100 hover:bg-primary-50 text-primary-600'
                                            }`}
                                        >
                                            <Plus size={16} />
                                            <span className="text-sm font-medium">Upload New Resume</span>
                                        </div>
                                    )}

                                    {resumes.length >= RESUME_CAP && (
                                        <div className={`px-4 py-2 text-xs border-t ${
                                            isDarkMode ? 'border-slate-600 text-slate-400' : 'border-slate-100 text-slate-500'
                                        }`}>
                                            Resume limit reached ({RESUME_CAP}/{RESUME_CAP}). Delete one to upload a new resume.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Delete confirmation modal */}
                        {deleteResumeConfirm && (
                            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50" onClick={() => setDeleteResumeConfirm(null)}>
                                <div
                                    className={`w-full max-w-md mx-4 rounded-lg border shadow-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Delete Resume</h3>
                                        <button onClick={() => setDeleteResumeConfirm(null)} className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}>
                                            <X size={18} />
                                        </button>
                                    </div>

                                    <div className="px-5 py-4">
                                        <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                            Are you sure you want to delete <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{deleteResumeConfirm.name}</span>? This cannot be undone.
                                        </p>
                                    </div>

                                    <div className={`flex justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <button
                                            onClick={() => setDeleteResumeConfirm(null)}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                                isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                            }`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => handleResumeDelete(deleteResumeConfirm.id)}
                                            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Upload modal */}
                        {showResumeUploadModal && (
                            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50" onClick={closeResumeUploadModal}>
                                <div
                                    className={`w-full max-w-md mx-4 rounded-lg border shadow-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Upload Resume</h3>
                                        <button onClick={closeResumeUploadModal} className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}>
                                            <X size={18} />
                                        </button>
                                    </div>

                                    <div className="px-5 py-4 space-y-4">
                                        <div>
                                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                Resume Label <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder='e.g. "Marketing Resume"'
                                                value={resumeNickname}
                                                onChange={(e) => setResumeNickname(e.target.value)}
                                                maxLength={100}
                                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                                                    isDarkMode
                                                        ? 'bg-slate-900 border-slate-600 text-slate-100 placeholder-slate-500'
                                                        : 'border-slate-300 text-slate-900 placeholder-slate-400'
                                                }`}
                                            />
                                        </div>

                                        <div
                                            onDragOver={(e) => { e.preventDefault(); setDraggingResume(true); }}
                                            onDragLeave={() => setDraggingResume(false)}
                                            onDrop={(e) => { e.preventDefault(); setDraggingResume(false); handleResumeFileSelect(e.dataTransfer.files[0]); }}
                                            className={`relative flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                                draggingResume
                                                    ? 'border-primary-500 bg-primary-50/10'
                                                    : pendingResumeFile
                                                        ? isDarkMode ? 'border-primary-600 bg-primary-900/20' : 'border-primary-400 bg-primary-50'
                                                        : isDarkMode ? 'border-slate-600 hover:border-primary-500 hover:bg-slate-700' : 'border-slate-300 hover:border-primary-500 hover:bg-primary-50'
                                            }`}
                                            onClick={() => resumeFileInputRef.current?.click()}
                                        >
                                            {pendingResumeFile ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <FileText size={24} className="text-primary-500" />
                                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{pendingResumeFile.name}</span>
                                                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        {(pendingResumeFile.size / 1024 / 1024).toFixed(2)} MB — click or drag to replace
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                    <Upload size={24} className="text-slate-400" />
                                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                        Drag & drop your resume here
                                                    </span>
                                                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                        or click to browse
                                                    </span>
                                                </div>
                                            )}
                                            <input
                                                ref={resumeFileInputRef}
                                                type="file"
                                                className="hidden"
                                                accept=".pdf,.docx"
                                                onChange={(e) => handleResumeFileSelect(e.target.files[0])}
                                                disabled={uploadingResume}
                                            />
                                        </div>

                                        {resumeError && (
                                            <p className="text-sm text-red-500 flex items-center gap-1">
                                                <X size={14} /> {resumeError}
                                            </p>
                                        )}
                                    </div>

                                    <div className={`flex justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <button
                                            onClick={closeResumeUploadModal}
                                            disabled={uploadingResume}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                                isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                            }`}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleResumeUploadSubmit}
                                            disabled={uploadingResume || !pendingResumeFile || !resumeNickname.trim()}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors flex items-center gap-2 ${
                                                uploadingResume || !pendingResumeFile || !resumeNickname.trim()
                                                    ? 'bg-primary-400 cursor-not-allowed'
                                                    : 'bg-primary-600 hover:bg-primary-700'
                                            }`}
                                        >
                                            {uploadingResume ? <><Loader2 size={16} className="animate-spin" /> Uploading...</> : <><Upload size={16} /> Upload</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PDF preview modal */}
                        {resumePreviewUrl && (
                            <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60" onClick={closeResumePreview}>
                                <div className="relative w-full max-w-4xl h-[85vh] mx-4" onClick={(e) => e.stopPropagation()}>
                                    <div className={`flex items-center justify-between px-4 py-3 rounded-t-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                                        <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{resumePreviewName}</span>
                                        <button onClick={closeResumePreview} className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <iframe src={resumePreviewUrl} className="w-full h-full rounded-b-lg" title="Resume Preview" />
                                </div>
                            </div>
                        )}
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Target Industries Multi-Select */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <Building2 className="text-primary-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Target Industries</h3>
                        </div>
                        <MultiSelect
                            label="Select Target Industries"
                            options={Object.keys(lookups.industries || {})
                                // "Default_Industry" / "Testingediting&add" are seeded
                                // placeholder rows in the Industries table, not real
                                // selectable options.
                                .filter(name => !['defaultindustry', 'testingeditingadd'].includes(name.replace(/[\s_&]/g, '').toLowerCase()))}
                            selected={formData.industries}
                            onChange={(selected) => setFormData({ ...formData, industries: selected })}
                            placeholder="Select one or more target industries..."
                            searchable
                            searchPlaceholder="Search industries..."
                            isDarkMode={isDarkMode}
                        />
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Position Title Multi-Select */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <Briefcase className="text-purple-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Position Title</h3>
                        </div>
                        <MultiSelect
                            label="Select Position Titles (Minimum 1 Required)"
                            options={availablePositions}
                            selected={formData.positions}
                            onChange={(selected) => setFormData({ ...formData, positions: selected })}
                            placeholder="Select at least one position title (e.g., Senior Programmer, Database Engineer)..."
                            required
                            minSelections={1}
                            error={formData.positions.length === 0 ? 'Please select at least one position title' : ''}
                            searchable
                            searchPlaceholder="Search position titles..."
                            isDarkMode={isDarkMode}
                        />
                        <p className={`text-sm mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            These position titles are managed by administrators and can be updated from the admin dashboard.
                        </p>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Location Multi-Select */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <MapPin className="text-amber-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Location</h3>
                        </div>
                        <MultiSelect
                            label="Preferred Locations (up to 3)"
                            options={availableLocations}
                            selected={formData.jobLocations}
                            onChange={(selected) => setFormData({ ...formData, jobLocations: selected.slice(0, 3) })}
                            placeholder="Select preferred locations (e.g., California, San Francisco)..."
                            searchable
                            searchPlaceholder="Search by state or city..."
                            isDarkMode={isDarkMode}
                        />
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Theme Preference Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            {isDarkMode ? <Moon className="text-indigo-600" size={24} /> : <Sun className="text-amber-600" size={24} />}
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Theme Preference</h3>
                        </div>
                        <div className="space-y-4">
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Choose your preferred theme for the application.</p>
                            <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                <label className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 border-2 rounded-xl md:rounded-2xl cursor-pointer transition-all ${
                                    !isDarkMode 
                                        ? 'border-blue-500 bg-blue-50' 
                                        : isDarkMode
                                            ? 'border-slate-600 hover:bg-slate-700'
                                            : 'border-slate-200 hover:bg-slate-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="theme"
                                        value="light"
                                        checked={!isDarkMode}
                                        onChange={() => setDarkMode(false)}
                                        className="w-5 h-5 text-blue-600"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Sun size={20} className={!isDarkMode ? 'text-blue-600' : (isDarkMode ? 'text-slate-400' : 'text-slate-400')} />
                                        <span className={`text-sm md:text-base font-medium ${
                                            !isDarkMode
                                                ? 'text-blue-900'
                                                : isDarkMode
                                                    ? 'text-slate-300'
                                                    : 'text-slate-700'
                                        }`}>Light Mode</span>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-2 md:gap-3 p-3 md:p-4 border-2 rounded-xl md:rounded-2xl cursor-pointer transition-all ${
                                    isDarkMode
                                        ? 'border-indigo-500 bg-indigo-900/30'
                                        : 'border-slate-200 hover:bg-slate-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="theme"
                                        value="dark"
                                        checked={isDarkMode}
                                        onChange={() => setDarkMode(true)}
                                        className="w-5 h-5 text-indigo-600"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Moon size={20} className={isDarkMode ? 'text-indigo-400' : 'text-slate-400'} />
                                        <span className={`text-sm md:text-base font-medium ${
                                            isDarkMode
                                                ? 'text-indigo-200'
                                                : 'text-slate-700'
                                        }`}>Dark Mode</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Security Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <Lock className="text-slate-500" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Security</h3>
                        </div>
                        <div className="space-y-4 max-w-md">
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Change your account password.</p>
                            <div className="relative">
                                <input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    placeholder="Current password"
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                    className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm transition-all focus:outline-none focus:ring-4 focus:ring-blue-400/50 ${
                                        isDarkMode
                                            ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400'
                                            : 'border-slate-300 bg-white text-slate-900 placeholder-slate-500'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                                        isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                                    tabIndex={-1}
                                >
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            <div>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        placeholder="New password"
                                        value={passwordForm.newPassword}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                        className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm transition-all focus:outline-none focus:ring-4 focus:ring-blue-400/50 ${
                                            isDarkMode
                                                ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400'
                                                : 'border-slate-300 bg-white text-slate-900 placeholder-slate-500'
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                                            isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
                                        }`}
                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                        tabIndex={-1}
                                    >
                                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {passwordForm.newPassword && (
                                    <p className={`mt-1 ml-1 text-xs font-medium ${
                                        newPasswordMeetsRequirements ? 'text-primary-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')
                                    }`}>
                                        {newPasswordMeetsRequirements ? '✓ Requirements met' : 'Requires 8+ characters and one uppercase letter'}
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm new password"
                                        value={passwordForm.confirmPassword}
                                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                        className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm transition-all focus:outline-none focus:ring-4 focus:ring-blue-400/50 ${
                                            isDarkMode
                                                ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400'
                                                : 'border-slate-300 bg-white text-slate-900 placeholder-slate-500'
                                        }`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                                            isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
                                        }`}
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                        tabIndex={-1}
                                    >
                                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {passwordForm.confirmPassword && (
                                    <p className={`mt-1 ml-1 text-xs font-medium ${confirmPasswordMatches ? 'text-primary-500' : 'text-red-500'}`}>
                                        {confirmPasswordMatches ? '✓ Passwords match' : 'Passwords do not match'}
                                    </p>
                                )}
                            </div>
                            {passwordError && (
                                <div className={`border px-4 py-3 rounded-lg text-sm ${
                                    isDarkMode
                                        ? 'bg-red-900/30 border-red-700 text-red-300'
                                        : 'bg-red-50 border-red-200 text-red-700'
                                }`}>
                                    {passwordError}
                                </div>
                            )}
                            {passwordSuccess && (
                                <div className={`border px-4 py-3 rounded-lg text-sm ${
                                    isDarkMode
                                        ? 'bg-green-900/30 border-green-700 text-green-300'
                                        : 'bg-green-50 border-green-200 text-green-700'
                                }`}>
                                    {passwordSuccess}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={handleChangePassword}
                                disabled={passwordSaving}
                                className={`flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-2xl font-bold hover:bg-slate-800 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <Lock size={18} /> {passwordSaving ? 'Updating...' : 'Update Password'}
                            </button>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Career Intent Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4 md:mb-6">
                            <Target className="text-primary-600" size={24} />
                            <h3 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Career Intent</h3>
                        </div>
                        <div className="space-y-4">
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>What is your primary goal using My BizBrand right now?</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                {Object.keys(lookups.intents || {})
                                    .filter(intent => intent.replace(/[\s_]/g, '').toLowerCase() !== 'defaultintent')
                                    .map(intent => (
                                    <label key={intent} className={`flex items-center gap-3 px-3 py-2.5 md:p-4 border rounded-xl md:rounded-2xl cursor-pointer transition-colors ${
                                        isDarkMode
                                            ? 'border-slate-600 hover:bg-slate-700'
                                            : 'border-slate-200 hover:bg-slate-50'
                                    }`}>
                                        <input
                                            type="radio"
                                            name="intent"
                                            value={intent}
                                            checked={formData.intent === intent}
                                            onChange={(e) => setFormData({ ...formData, intent: e.target.value })}
                                            className="w-4 h-4 md:w-5 md:h-5 shrink-0 text-blue-600"
                                        />
                                        <span className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{intent}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Error and Success Messages */}
                    {error && (
                        <div className={`border px-4 py-3 rounded-lg ${
                            isDarkMode 
                                ? 'bg-red-900/30 border-red-700 text-red-300' 
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className={`border px-4 py-3 rounded-lg ${
                            isDarkMode 
                                ? 'bg-green-900/30 border-green-700 text-green-300' 
                                : 'bg-green-50 border-green-200 text-green-700'
                        }`}>
                            {success}
                        </div>
                    )}

                    {/* Save Button */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={saving || formData.positions.length === 0}
                            className={`flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                isDarkMode ? 'shadow-blue-900/30' : 'shadow-blue-200'
                            }`}
                        >
                            <Save size={20} /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>

            {showDisconnectConfirm && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
                    <div
                        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => setShowDisconnectConfirm(false)}
                    />
                    <div className={`relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 p-6 ${
                        isDarkMode ? 'bg-slate-800' : 'bg-white'
                    }`}>
                        <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
                            isDarkMode ? 'bg-red-900/30' : 'bg-red-50'
                        }`}>
                            <Lock className={isDarkMode ? 'text-red-400' : 'text-red-600'} size={22} />
                        </div>
                        <h3 className={`text-lg font-bold text-center ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Disconnect LinkedIn?
                        </h3>
                        <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            Your LinkedIn URL will be removed from your account. You can reconnect it any time.
                        </p>
                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDisconnectConfirm(false)}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                                    isDarkMode
                                        ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDisconnectLinkedIn}
                                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
                            >
                                Disconnect
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
